import { IntegrationsTasksItem } from "frontend/canvas-designer-new/integrations/integrations-tasks-item";
import { IntegrationsMappingSettings } from "frontend/canvas-designer-new/integrations/side-panel/mapping-settings/mapping-settings";
import SyncMondayBoardModal from "frontend/canvas-designer-new/integrations/modals/sync-monday-board/sync-monday-board-modal";
import { useDebounce } from "frontend/hooks/debounce";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { BoardIntegrationLoadingState, IntegrationData } from "frontend/hooks/use-board-integrations.types";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { CloseIcon } from "frontend/icons/close";
import { MondayLogo } from "frontend/icons/monday-logo";
import { useModal } from "frontend/modal/dynamic-modal";
import { SyncService } from "frontend/services/syncService";
import tracking from "frontend/tracking";
import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import StyledButton, { ButtonStyle } from "frontend/ui-components/styled-button";
import { useAtom, useAtomValue } from "jotai";
import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import consts from "shared/consts";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { IntegrationType } from "shared/integrations/integration";
import { createIntegrationTypeAtom, mondayIntegrationPopupAtom } from "state-atoms";
import { getPathPrefix } from "../../../utils/getPathPrefix";
import { MondayListItem } from "./monday-list-item";
import SubitemsTaskList from "./subitems-task-list";
import style from "./monday-integration-side-panel.module.css";
import { useIsCIMReorderEnabled } from "utils/url-utils";
import { Box } from "frontend/ui-components/design-system/box/box";
import cn from "classnames";
import { SearchIcon } from "frontend/icons/search";
import { TextInput } from "frontend/ui-components/design-system/text-input";
import { MondayListItemV2 } from "./monday-list-item-v2";

export default function MondayIntegrationSidePanel({
  documentId,
  onChooseItems,
  pusherChannel,
  onClose,
  syncService,
}: {
  documentId?: string;
  onChooseItems: (items: { integrationId: string; integrationType: IntegrationType; configuration: any }[]) => void;
  pusherChannel?: any;
  onClose?: () => void;
  syncService: SyncService<RW> | null;
}) {
  const {
    shouldAddTasksBoard,
    mondayIntegration,
    tasksIntegrations,
    dataSource,
    isLoaded: isTasksLoaded,
    getBoardIntegrationConfig,
    getLoadingState,
    fetchItems,
  } = useBoardTasks(documentId, pusherChannel);
  const [showMondayIntegrationPopup, setShowMondayIntegrationPopup] = useAtom(mondayIntegrationPopupAtom);
  const createIntegrationType = useAtomValue(createIntegrationTypeAtom);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string | null>(null);
  const [checkedItemIds, setCheckedItemIds] = useState<{ [boardId: string]: string[] }>({});
  const [itemsQuery, setItemsQuery] = useState("");
  const [showingItemSubitems, setShowingItemSubitems] = useState<any>();
  const debouncedItemsQuery = useDebounce(itemsQuery, 200);
  const isCimReorderEnabled = useIsCIMReorderEnabled();

  const [showSettings, setShowSettings] = useState(false);
  const totalCheckedItemIds = useMemo(
    () => Object.values(checkedItemIds).reduce((acc, ids) => acc + ids.length, 0),
    [checkedItemIds]
  );
  const selectedIntegrationData = dataSource && selectedIntegrationId ? dataSource[selectedIntegrationId] : null;
  const selectedIntegrationConfig = selectedIntegrationId ? getBoardIntegrationConfig(selectedIntegrationId) : null;
  const featureFlag = useFeatureFlag("gantt-monday-integration-items");
  const listRef = useRef<HTMLDivElement>(null);

  const newMondayTaskEnabled = useFeatureFlag("new-monday-task-element");

  const { showModal } = useModal();

  useEffect(() => {
    if (!tasksIntegrations || tasksIntegrations.length === 0 || selectedIntegrationId) {
      return;
    }
    const firstItemsIntegration = tasksIntegrations.find((integration) => !integration.configuration.isSubitemsBoard);
    if (!firstItemsIntegration) {
      return;
    }
    const id = firstItemsIntegration?.id;
    setSelectedIntegrationId(id);
    fetchItems({ [id]: { limit: consts.INTEGRATIONS_ITEMS_LIMIT } });
  }, [tasksIntegrations, selectedIntegrationId]);

  useEffect(() => {
    return () => {
      setShowMondayIntegrationPopup({
        isOpen: false,
      });
    };
  }, []);

  useEffect(() => {
    if (
      showMondayIntegrationPopup &&
      showMondayIntegrationPopup.integrationId &&
      showMondayIntegrationPopup.selectedResult === "map"
    ) {
      setSelectedIntegrationId(showMondayIntegrationPopup.integrationId);
      setShowSettings(true);
      setShowingItemSubitems(undefined);
    }
  }, [showMondayIntegrationPopup]);

  useEffect(() => {
    if (debouncedItemsQuery && selectedIntegrationId) {
      fetchItems({
        [selectedIntegrationId]: { limit: consts.INTEGRATIONS_ITEMS_QUERY_LIMIT, query: debouncedItemsQuery },
      });
    }
  }, [selectedIntegrationId, debouncedItemsQuery]);

  const handleScroll = useCallback(() => {
    // handle scrolling of listRef, when at the end, load more items
    if (!listRef.current || !selectedIntegrationId || !selectedIntegrationData) {
      return;
    }
    const isAlreadyLoading = getLoadingState(selectedIntegrationId) === BoardIntegrationLoadingState.loading;
    const isFiltering = !!itemsQuery;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const nextPageCursor = selectedIntegrationData.pageCursor;
    if (nextPageCursor && !isAlreadyLoading && !isFiltering && scrollTop + clientHeight >= scrollHeight - 120) {
      fetchItems({ [selectedIntegrationId]: { pageCursor: nextPageCursor, limit: consts.INTEGRATIONS_ITEMS_LIMIT } });
    }
  }, [listRef.current, selectedIntegrationId, selectedIntegrationData, getLoadingState, itemsQuery]);

  const debouncedScroll = useMemo(() => debounce(handleScroll, 100), [handleScroll]);

  useEffect(() => {
    const currentRef = listRef.current;
    currentRef?.addEventListener("scroll", debouncedScroll);
    return () => currentRef?.removeEventListener("scroll", debouncedScroll);
  }, [debouncedScroll]);

  function chooseItems() {
    if (!totalCheckedItemIds) {
      return;
    }
    tracking.trackEvent(consts.TRACKING_CATEGORY.INTEGRATIONS, "add_multiple_items", totalCheckedItemIds);
    const chosenItems = Object.entries(checkedItemIds).reduce((acc, [boardId, ids]) => {
      const integration = tasksIntegrations?.find((integration) => integration.configuration.boardId === boardId);
      if (!integration) {
        return acc;
      }
      const items = ids.map((id) => ({
        integrationId: integration.id,
        integrationType: IntegrationType.monday,
        configuration: { itemId: id },
      }));
      return [...acc, ...items];
    }, [] as { integrationId: string; integrationType: IntegrationType; configuration: any }[]);
    onChooseItems(chosenItems);
    setCheckedItemIds({});
  }

  const onSelectIntegration = useCallback(({ integrationId }: { integrationId: string }) => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.INTEGRATIONS, "board_selected", integrationId);
    const { pageCursor, items, partial } = (dataSource && dataSource[integrationId]) || {};
    setSelectedIntegrationId(integrationId);
    if (!pageCursor && items && !partial) {
      // already loaded all items
      return;
    }
    if (!items || partial) {
      fetchItems({ [integrationId]: { pageCursor, limit: consts.INTEGRATIONS_ITEMS_LIMIT } });
    }
    listRef.current?.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mondayIntegration || !createIntegrationType) return;
    showModal(SyncMondayBoardModal, onSelectIntegration, {
      integrationId: mondayIntegration?.id,
    });
  }, [createIntegrationType, mondayIntegration, onSelectIntegration, showModal]);

  function importClicked() {
    tracking.trackEvent(consts.TRACKING_CATEGORY.INTEGRATIONS, "import_board_clicked_from_empty_state");
    if (shouldAddTasksBoard) {
      showModal(SyncMondayBoardModal, onSelectIntegration, {
        integrationId: mondayIntegration?.id,
      });
    }
  }

  function addBoardClicked() {
    tracking.trackEvent(consts.TRACKING_CATEGORY.INTEGRATIONS, "add_board_clicked");
    if (!mondayIntegration) return;

    showModal(SyncMondayBoardModal, onSelectIntegration, {
      integrationId: mondayIntegration.id,
    });
  }

  function _renderTasksList(integrationId: string, data: IntegrationData | null, configuration: unknown) {
    if (!dataSource || !data) {
      return renderLoading();
    }
    const filteredData = itemsQuery
      ? data.items.filter((item) => item.name.toLowerCase().includes(itemsQuery))
      : data.items;

    const isLoading = getLoadingState(integrationId) === BoardIntegrationLoadingState.loading;
    const hasMoreItems = !!data.pageCursor || isLoading;
    const showLoadingMore = hasMoreItems && !itemsQuery;

    if (filteredData.length === 0 && itemsQuery) {
      return (
        <div className={style.noTasksContainer}>
          <img src={getPathPrefix("/images/board-integrations-empty-state.png")} alt="No items found" />
          <span className={style.emptyStateTitle}>No items found for term &quot;{itemsQuery}&quot;</span>
        </div>
      );
    }

    const selectedItemIds = checkedItemIds[data.boardId] || [];

    return (
      <>
        <div ref={listRef} className={cn(style.tasksList, isCimReorderEnabled && style.cimTasksList)}>
          {filteredData.map((item) => {
            const props = {
              item: item,
              configuration: configuration,
              isChecked: selectedItemIds.includes(item.id),
              integrationId: integrationId,
              integrationType: data.type,
              showCheckbox: selectedItemIds.length > 0,
              onClick: () =>
                setCheckedItemIds((previous) => {
                  const isChecked = previous[data.boardId]?.includes(item.id);
                  return {
                    ...previous,
                    [data.boardId]: isChecked
                      ? previous[data.boardId]?.filter((id) => id !== item.id) || []
                      : [...(previous[data.boardId] || []), item.id],
                  };
                }),
              onClickSubitems: () => setShowingItemSubitems(item),
              onDragStart: () => {
                setCheckedItemIds({});
              },
            };

            if (newMondayTaskEnabled) {
              return <MondayListItemV2 key={item.id} {...props} />;
            }

            return <MondayListItem key={item.id} {...props} />;
          })}
          {showLoadingMore && <Skeleton height={120} style={{ marginBottom: 12, borderRadius: 0 }} />}
        </div>
      </>
    );
  }

  const renderTasksList = useCallback(_renderTasksList, [dataSource, checkedItemIds, itemsQuery, getLoadingState]);

  function renderLoading() {
    return (
      <Skeleton
        containerClassName={style.skeletonContainer}
        count={4}
        height={120}
        style={{ marginBottom: 12, borderRadius: 0 }}
      />
    );
  }

  function renderEmptyState() {
    return (
      <div className={style.subcontainer}>
        <CloseIcon
          onClick={() => {
            onClose?.();
          }}
          className={style.closeIcon}
        />
        <div className={style.noIntegrationContainer}>
          <img src={getPathPrefix("/images/board-integrations-empty-state.png")} alt="No integration found" />
          <span className={style.emptyStateTitle}>
            Integrate with your Monday account, select a board and import your items
          </span>
          <StyledButton title={"Import items"} onClick={importClicked} style={ButtonStyle.outline} />
        </div>
      </div>
    );
  }

  function renderIntegrationOptions({ option, selected }: { option: string; selected: boolean }): {
    element: ReactNode;
    enabled: boolean;
    customStyle?: CSSProperties;
    handlesSelectedState?: boolean;
    customIcon?: string;
    wrapWithContainer?: boolean;
  } {
    if (option === "add") {
      return {
        element: "+ Add another board",
        enabled: true,
        handlesSelectedState: true,
      };
    }
    if (!tasksIntegrations) {
      return {
        element: null,
        enabled: true,
        handlesSelectedState: true,
      };
    }
    const integration = tasksIntegrations.find((integration) => integration.id === option);

    if (!selected) {
      return {
        element: integration?.name,
        enabled: true,
        handlesSelectedState: true,
      };
    }

    return {
      element: (
        <IntegrationsTasksItem
          name={integration?.name || ""}
          isSelected={selected}
          onSettingsClicked={() => {
            setShowSettings(true);
          }}
        />
      ),
      enabled: true,
      handlesSelectedState: true,
    };
  }

  function renderContent() {
    const shouldShowAddIntegration = shouldAddTasksBoard;
    if ((isTasksLoaded && shouldShowAddIntegration) || !tasksIntegrations) {
      return renderEmptyState();
    }

    const allIntegrations = tasksIntegrations
      .filter((integration) => !integration.configuration.isSubitemsBoard)
      .map((integration) => integration.id);
    allIntegrations.push("add");

    return (
      <div className={style.subcontainer}>
        {isCimReorderEnabled && !showSettings && (
          <div className={style.mappingMondayHeaderContainer}>
            <div className={style.mappingMondayHeader}>
              <MondayLogo size={20} />
              <div>Import</div>
            </div>
            <CloseIcon
              onClick={() => {
                onClose?.();
              }}
            />
          </div>
        )}
        {!isCimReorderEnabled && (
          <CloseIcon
            onClick={() => {
              onClose?.();
            }}
            className={style.closeIcon}
          />
        )}
        <div
          style={{
            marginTop: isCimReorderEnabled ? "0px" : "30px",
          }}
          className={cn(style.subcontainer, isCimReorderEnabled && style.cimSubcontainer)}
        >
          {showSettings && featureFlag && (
            <div className={style.mappingMondayHeader}>
              <MondayLogo size={20} />
              <div>Gantt integration settings</div>
            </div>
          )}

          <div className={style.header}>
            {isCimReorderEnabled ? null : <div className={style.mappingMondayHeaderSubTitle}>Choose board</div>}
            <DropdownPicker
              options={allIntegrations}
              isSelected={(id) => selectedIntegrationId === id}
              titles={(id) =>
                id === "add"
                  ? "+ Add another board"
                  : tasksIntegrations.find((integration) => integration.id === id)!.name
              }
              enabled={isTasksLoaded}
              onChange={(id) => {
                setItemsQuery("");
                id === "add" ? addBoardClicked() : onSelectIntegration({ integrationId: id });
              }}
              optionsPickerStyle={{
                fontSize: 14,
                maxWidth: 250,
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
              optionRenderer={featureFlag ? renderIntegrationOptions : undefined}
              styles={{
                optionClassName: style.selectedOption,
                rounded: true,
                pickerParent: style.dropdownPicker,
              }}
              pickerCustomStyle={
                isCimReorderEnabled
                  ? {
                      backgroundColor: "var(--gray-bubble)",
                      border: "none",
                      color: "#1e1e1e",
                    }
                  : {}
              }
            />
            {showSettings ? null : isCimReorderEnabled ? (
              <TextInput
                leftIcon={<SearchIcon />}
                variant="small"
                placeholder="Search items..."
                onChange={(e) => setItemsQuery(e.target.value.toLowerCase())}
              />
            ) : (
              !showSettings && (
                <div className={style.searchContainer}>
                  <input
                    type="text"
                    value={itemsQuery}
                    placeholder="Search items..."
                    onChange={(e) => setItemsQuery(e.target.value.toLowerCase())}
                  />
                </div>
              )
            )}
          </div>
          {!showSettings && <div className={style.hr} />}
          {showSettings && featureFlag && (
            <IntegrationsMappingSettings
              syncService={syncService}
              documentId={documentId}
              selectedIntegrationId={selectedIntegrationId}
              onDismiss={() => {
                setShowSettings(false);
                setShowMondayIntegrationPopup({
                  isOpen: false,
                  integrationId: undefined,
                  selectedResult: undefined,
                });
              }}
            ></IntegrationsMappingSettings>
          )}
          {!showSettings &&
            (isTasksLoaded
              ? renderTasksList(selectedIntegrationId!, selectedIntegrationData, selectedIntegrationConfig)
              : renderLoading())}
        </div>
      </div>
    );
  }

  function renderItemSubitems(item: any) {
    if (!mondayIntegration || !selectedIntegrationData?.subitemsBoard) {
      return null;
    }

    const subitemBoard = selectedIntegrationData.subitemsBoard;
    return (
      <SubitemsTaskList
        documentId={documentId!}
        parentBoardId={selectedIntegrationData.boardId}
        subitemsBoard={selectedIntegrationData.subitemsBoard}
        item={item}
        onBack={() => setShowingItemSubitems(undefined)}
        onCheckItem={(id) => {
          setCheckedItemIds((previous) => {
            const isChecked = previous[subitemBoard.id]?.includes(id);
            return {
              ...previous,
              [subitemBoard.id]: isChecked
                ? previous[subitemBoard.id]?.filter((itemId) => itemId !== id) || []
                : [...(previous[subitemBoard.id] || []), id],
            };
          });
        }}
        checkedItems={checkedItemIds[selectedIntegrationData.subitemsBoard.id] || []}
      />
    );
  }

  if (isCimReorderEnabled) {
    return (
      <Box className={style.cimContainer}>
        {showingItemSubitems ? renderItemSubitems(showingItemSubitems) : renderContent()}
        {totalCheckedItemIds > 0 && !showSettings && (
          <div className={cn(style.actions, isCimReorderEnabled && style.cimActions)} data-testid="add-items-to-canvas">
            <StyledButton
              title={totalCheckedItemIds > 1 ? `Add ${totalCheckedItemIds} items to canvas` : `Add to canvas`}
              onClick={chooseItems}
              enabled={totalCheckedItemIds > 0}
            />
          </div>
        )}
      </Box>
    );
  }

  return (
    <div
      className={style.container}
      style={{
        paddingTop: document.getElementById("app-header-container")?.clientHeight || 100,
      }}
    >
      {showingItemSubitems ? renderItemSubitems(showingItemSubitems) : renderContent()}
      {totalCheckedItemIds > 0 && !showSettings && (
        <div className={cn(style.actions, isCimReorderEnabled && style.cimActions)} data-testid="add-items-to-canvas">
          <StyledButton
            title={totalCheckedItemIds > 1 ? `Add ${totalCheckedItemIds} items to canvas` : `Add to canvas`}
            onClick={chooseItems}
            enabled={totalCheckedItemIds > 0}
          />
        </div>
      )}
    </div>
  );
}

// TODO: I don't know why this file has its own implementation of debounce.
// We have 2 libraries and countless implementations in the codebase.
// We should probably dedupe this.
const debounce = (function_: any, wait: number, immediate = false) => {
  let timeout: any;
  return function (this_: any) {
    const context = this_,
      // eslint-disable-next-line prefer-rest-params
      args = arguments;
    const later = function () {
      timeout = null;
      if (!immediate) function_.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) function_.apply(context, args);
  };
};
